import Phaser from "phaser";
import { LevelId } from "../state/GameState";
import { levelById } from "../levels";
import { Palette } from "../ui/palette";
import { Tex } from "../ui/textures";
import { makeButton, starRow } from "../ui/widgets";
import { POST_LEVEL_CUTSCENE } from "../data/cutscenes";

interface ResultData {
  levelId: LevelId;
  stars: number;
  lines: string[];
  failed?: boolean;
  sheepy?: boolean;
  regionConquered?: boolean;
  score?: {
    collectibles: number;
    enemies: number;
    bonus: number;
    speed: number;
    sheepy: number;
    total: number;
  };
}

/** Shown after a level ends: stars, a short narrative recap, and where to go. */
export class LevelResultScene extends Phaser.Scene {
  constructor() {
    super("LevelResult");
  }

  create(data: ResultData) {
    const { width, height } = this.scale;
    const meta = levelById(data.levelId);

    this.add.rectangle(width / 2, height / 2, width, height, Palette.bg, 0.96);

    this.add
      .text(width / 2, 70, data.failed ? "TRY AGAIN" : `${meta.title} - Complete`, {
        fontFamily: "monospace",
        fontSize: "34px",
        color: data.failed ? "#ff8080" : Palette.text,
      })
      .setOrigin(0.5);

    if (!data.failed) starRow(this, width / 2, 140, data.stars, 3, 1.4);

    const body = (data.lines ?? []).join("\n");
    this.add
      .text(width / 2, 208, body, {
        fontFamily: "monospace",
        fontSize: "15px",
        color: Palette.textDim,
        align: "center",
        lineSpacing: 6,
        wordWrap: { width: width - 160 },
      })
      .setOrigin(0.5);

    // Itemized score breakdown (a monospace table). Rendered only on a completed
    // level with a score payload. Sheepy is a scored line here; when found, its
    // sprite sits to the LEFT of the table so it never lands on the text.
    const showBreakdown = !data.failed && !!data.score;

    if (showBreakdown && data.score) {
      const s = data.score;
      const rows: Array<[string, number]> = [];
      if (s.collectibles) rows.push(["Collectibles", s.collectibles]);
      if (s.enemies) rows.push(["Enemies", s.enemies]);
      if (s.bonus) rows.push(["Throw & combo", s.bonus]);
      if (s.speed) rows.push(["Speed bonus", s.speed]);
      if (s.sheepy) rows.push(["Sheepy", s.sheepy]);
      rows.push(["Total", s.total]);

      // Pad into two aligned columns (labels left, values right).
      const width2 =
        Math.max(...rows.map(([label, value]) => label.length + String(value).length)) + 4;
      const line = (label: string, value: number) =>
        label + String(value).padStart(width2 - label.length);
      const sep = "-".repeat(width2);

      const bodyLines = rows.slice(0, -1).map(([l, v]) => line(l, v));
      const [totalLabel, totalValue] = rows[rows.length - 1];
      const table = [...bodyLines, sep, line(totalLabel, totalValue)].join("\n");

      // Anchor the table by its BOTTOM just above the Continue button so a tall
      // breakdown (now up to 5 rows + separator + total) never sits under it.
      const tableText = this.add
        .text(width / 2, height - 120, table, {
          fontFamily: "monospace",
          fontSize: "16px",
          color: Palette.text,
          align: "left",
          lineSpacing: 6,
        })
        .setOrigin(0.5, 1);

      // Celebratory Sheepy sprite to the left of the table, vertically centered on
      // it and clear of the text (the points already show in the Sheepy row).
      if (data.sheepy) {
        const b = tableText.getBounds();
        this.add.image(b.left - 46, b.centerY, Tex.sheepy).setScale(0.9).setOrigin(0.5);
      }
    } else if (data.sheepy) {
      // No breakdown payload (defensive fallback): keep the plain badge.
      this.add.image(width / 2 - 120, 330, Tex.sheepy).setScale(0.85);
      this.add
        .text(width / 2 - 60, 330, "Sheepy secured", { fontFamily: "monospace", fontSize: "14px", color: Palette.text })
        .setOrigin(0, 0.5);
    }

    if (data.failed) {
      makeButton(this, width / 2 - 130, height - 90, "↻  Retry", () => this.scene.start(meta.sceneKey), {
        width: 200,
        fill: meta.accent,
      });
      makeButton(this, width / 2 + 130, height - 90, "Level Select", () => this.scene.start("LevelSelect"), {
        width: 200,
      });
    } else {
      const cutscene = POST_LEVEL_CUTSCENE[data.levelId];
      let onContinue: () => void;
      if (cutscene && data.regionConquered) {
        onContinue = () => this.scene.start("Dialogue", { scriptKey: cutscene, next: "RegionConquered", nextData: { next: "LevelSelect" } });
      } else if (cutscene) {
        onContinue = () => this.scene.start("Dialogue", { scriptKey: cutscene, next: "LevelSelect" });
      } else if (data.regionConquered) {
        onContinue = () => this.scene.start("RegionConquered", { next: "LevelSelect" });
      } else {
        onContinue = () => this.scene.start("LevelSelect");
      }
      makeButton(this, width / 2, height - 90, "Continue  ▶", onContinue, {
        width: 240,
        fill: meta.accent,
      });
    }
  }
}
